import { Link } from "react-router-dom";
import { Flame, Lightbulb, Compass, Eye, Target, Handshake, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1708457753320-02e52a276a80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjByZWQlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzkxOTc0MDI1fDA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: Flame, title: "Ambition", text: "We work with brands that refuse to stay average — and we hold ourselves to the same standard." },
  { icon: Lightbulb, title: "Creativity", text: "Ideas that are not just original, but built to serve a strategy." },
  { icon: Compass, title: "Rigor", text: "Clear processes, precise execution, no improvisation." },
  { icon: Eye, title: "Transparency", text: "You always know what we do, why we do it and what it brings." },
  { icon: Target, title: "Results", text: "Every action is tied to a concrete business objective." },
  { icon: Handshake, title: "Long-term partnership", text: "We build relationships, not one-off campaigns." },
];

const PRINCIPLES = [
  { num: "01", title: "Strategy before tactics", text: "No channel, no content, no campaign without a clear plan behind it." },
  { num: "02", title: "Creativity with intent", text: "Every creative choice serves the brand and the objective." },
  { num: "03", title: "Execution with discipline", text: "Great strategies fail without rigorous, consistent delivery." },
  { num: "04", title: "Growth you can see", text: "We track, report and optimize so progress is always visible." },
];

export default function About() {
  return (
    <main>
      <Seo
        title="About — 4EMPIRE | We Turn Ambition Into Growth"
        description="4EMPIRE is a strategic partner for ambitious brands, not just another agency. Discover the values and principles behind our work."
      />

      <section className="relative overflow-hidden bg-empire-black pb-20 pt-40 lg:pb-28 lg:pt-48" data-testid="about-hero">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div className="halo-red pointer-events-none absolute -left-32 top-10 h-[420px] w-[420px] rounded-full" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">About 4EMPIRE</p>
            <h1 className="mt-4 max-w-3xl font-anton text-5xl uppercase leading-[0.98] sm:text-6xl" data-testid="about-heading">
              We turn ambition <span className="text-empire-red">into growth.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-empire-mist sm:text-lg">
              4EMPIRE is not just another agency. We are a strategic partner for businesses that
              want to move to the next level — combining strategy, creativity and execution to
              build stronger brands and sustainable growth.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mt-14">
              <div className="halo-red pointer-events-none absolute -inset-6" aria-hidden="true" />
              <img
                src={HERO_IMG}
                alt="Abstract red and black geometric composition representing the 4EMPIRE brand"
                loading="lazy"
                className="relative h-64 w-full border border-white/10 object-cover sm:h-80"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-empire-coal py-24 lg:py-32" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Our values</p>
            <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="values-heading">
              What we stand for.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.08} className="h-full">
                <div
                  className="group h-full border border-white/10 bg-empire-black/50 p-8 transition-colors duration-300 hover:border-empire-red/60"
                  data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <v.icon className="h-7 w-7 text-empire-red" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-6 font-anton text-lg uppercase tracking-wide">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-empire-mist">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-empire-black py-24 lg:py-32" data-testid="philosophy-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Our philosophy</p>
            <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="philosophy-heading">
              Average is not an option.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col bg-empire-black p-10" data-testid={`principle-${p.num}`}>
                  <span className="font-anton text-5xl text-empire-red" aria-hidden="true">{p.num}</span>
                  <h3 className="mt-5 font-anton text-xl uppercase tracking-wide">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-empire-mist">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-empire-coal py-24 lg:py-32" data-testid="about-cta">
        <div className="halo-red pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center lg:px-10">
          <Reveal>
            <h2 className="font-anton text-4xl uppercase leading-tight sm:text-5xl">
              Let&apos;s build <span className="text-empire-red">your empire.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-empire-mist">
              Tell us where you want to go. We&apos;ll build the strategy that takes you there.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="mt-10 inline-flex items-center gap-2 bg-empire-red px-10 py-5 font-grotesk text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
            >
              Start Your Project <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
